import React, { useState, useEffect } from 'react';
import { adminApi } from '../../services/api';
import { toast } from 'sonner';
import {
  Upload, FileText, Image as ImageIcon, Film, Package, Trash2,
  Edit2, Download, Link2, Copy, Loader2, X, Eye, EyeOff, Search, Plus,
} from 'lucide-react';


function categoryIcon(cat) {
  const m = {
    'Brochure': FileText,
    'Social Media': ImageIcon,
    'Presentazioni': FileText,
    'Listino prezzi': FileText,
    'Video': Film,
    'Altro': Package,
  };
  return m[cat] || Package;
}

function formatSize(bytes) {
  if (!bytes) return '';
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(0)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}


export default function SuperadminMarketingAssets() {
  const [assets, setAssets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState('');
  const [catFilter, setCatFilter] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminApi.get('/superadmin/marketing-assets');
      setAssets(r.data.assets || []);
      setCategories(r.data.categories || []);
    } catch {
      toast.error('Errore caricamento asset');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const remove = async (a) => {
    if (!window.confirm(`Eliminare "${a.title}"? Non sarà più disponibile per gli agenti.`)) return;
    try {
      await adminApi.delete(`/superadmin/marketing-assets/${a.id}`);
      toast.success('Asset eliminato');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const toggleActive = async (a) => {
    try {
      await adminApi.put(`/superadmin/marketing-assets/${a.id}`, { active: !a.active });
      toast.success(a.active ? 'Nascosto agli agenti' : 'Reso visibile');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const copyShareLink = (a) => {
    const url = `${window.location.origin}/api/public/marketing-assets/${a.share_token}`;
    navigator.clipboard.writeText(url);
    toast.success('Link copiato');
  };

  const filtered = assets.filter(a => {
    if (filter && !`${a.title} ${a.description}`.toLowerCase().includes(filter.toLowerCase())) return false;
    if (catFilter && a.category !== catFilter) return false;
    return true;
  });

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto" data-testid="sa-marketing-assets-page">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-[#1A1A1A] mb-1">Asset Marketing</h1>
          <p className="text-sm text-slate-500">
            Materiali che gli agenti commerciali possono scaricare o condividere con i clienti finali.
          </p>
        </div>
        <button
          onClick={() => setShowUpload(true)}
          data-testid="upload-asset-btn"
          className="px-4 py-2.5 bg-[#D44E20] text-white rounded-lg font-semibold hover:shadow-md flex items-center gap-2"
        >
          <Plus size={15} /> Carica asset
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-5">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={filter}
            onChange={e => setFilter(e.target.value)}
            placeholder="Cerca per titolo o descrizione..."
            data-testid="search-input"
            className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-[#D44E20]"
          />
        </div>
        <select
          value={catFilter}
          onChange={e => setCatFilter(e.target.value)}
          data-testid="category-filter"
          className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
        >
          <option value="">Tutte le categorie</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-[#D44E20]" /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center">
          <Upload size={32} className="mx-auto mb-2 text-slate-300" />
          <p className="text-sm text-slate-500">Nessun asset ancora. Caricane uno per iniziare.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(a => {
            const Icon = categoryIcon(a.category);
            return (
              <div
                key={a.id}
                className={`bg-white border rounded-2xl p-4 transition-opacity ${a.active ? 'border-slate-200' : 'border-slate-200 opacity-60'}`}
                data-testid={`asset-card-${a.id}`}
              >
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-[#FDECE7] flex items-center justify-center flex-shrink-0">
                    <Icon size={18} className="text-[#D44E20]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-[#1A1A1A] truncate">{a.title}</p>
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{a.category}</p>
                  </div>
                </div>
                {a.description && (
                  <p className="text-xs text-slate-600 mb-3 line-clamp-2">{a.description}</p>
                )}
                <div className="flex items-center justify-between text-[11px] text-slate-500 mb-3">
                  <span>{formatSize(a.size_bytes)} · {a.file_name}</span>
                  <span>{a.download_count || 0} download</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => copyShareLink(a)}
                    data-testid={`copy-share-${a.id}`}
                    className="flex-1 px-2.5 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-lg text-xs font-semibold flex items-center justify-center gap-1"
                  >
                    <Link2 size={11} /> Link
                  </button>
                  <button
                    onClick={() => toggleActive(a)}
                    data-testid={`toggle-active-${a.id}`}
                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600"
                    title={a.active ? 'Nascondi agli agenti' : 'Rendi visibile'}
                  >
                    {a.active ? <Eye size={13} /> : <EyeOff size={13} />}
                  </button>
                  <button
                    onClick={() => setEditing(a)}
                    data-testid={`edit-${a.id}`}
                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600"
                  >
                    <Edit2 size={13} />
                  </button>
                  <button
                    onClick={() => remove(a)}
                    data-testid={`delete-${a.id}`}
                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-50 hover:bg-red-100 text-red-600"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showUpload && (
        <UploadModal
          categories={categories}
          onClose={() => setShowUpload(false)}
          onDone={load}
        />
      )}
      {editing && (
        <EditModal
          asset={editing}
          categories={categories}
          onClose={() => setEditing(null)}
          onDone={load}
        />
      )}
    </div>
  );
}


function UploadModal({ categories, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState(categories[0] || 'Altro');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!file || !title.trim()) { toast.error('Seleziona file e inserisci il titolo'); return; }
    setSaving(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('title', title);
    fd.append('description', description);
    fd.append('category', category);
    try {
      await adminApi.post('/superadmin/marketing-assets', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Asset caricato');
      onDone();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore upload'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="upload-modal">
      <div className="bg-white rounded-2xl w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <h3 className="text-lg font-bold">Carica nuovo asset</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Titolo*</label>
            <input
              value={title}
              onChange={e => setTitle(e.target.value)}
              data-testid="upload-title"
              placeholder="Es. Brochure Lazo 2026"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Descrizione</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              data-testid="upload-description"
              placeholder="Breve descrizione per gli agenti"
              rows={3}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Categoria</label>
            <select
              value={category}
              onChange={e => setCategory(e.target.value)}
              data-testid="upload-category"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
            >
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">File* (max 100 MB)</label>
            <input
              type="file"
              onChange={e => setFile(e.target.files?.[0])}
              data-testid="upload-file"
              className="w-full text-sm file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-slate-100 file:font-semibold hover:file:bg-slate-200"
            />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 p-5 border-t border-slate-200 bg-slate-50">
          <button onClick={onClose} className="px-4 py-2 border border-slate-200 rounded-lg text-sm font-semibold">Annulla</button>
          <button
            onClick={submit}
            disabled={saving}
            data-testid="upload-submit"
            className="px-5 py-2 bg-[#D44E20] text-white rounded-lg text-sm font-semibold hover:shadow-md disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <><Upload size={13} /> Carica</>}
          </button>
        </div>
      </div>
    </div>
  );
}


function EditModal({ asset, categories, onClose, onDone }) {
  const [title, setTitle] = useState(asset.title);
  const [description, setDescription] = useState(asset.description || '');
  const [category, setCategory] = useState(asset.category);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!title.trim()) { toast.error('Titolo obbligatorio'); return; }
    setSaving(true);
    try {
      await adminApi.put(`/superadmin/marketing-assets/${asset.id}`, {
        title, description, category,
      });
      toast.success('Asset aggiornato');
      onDone();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="edit-modal">
      <div className="bg-white rounded-2xl w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <h3 className="text-lg font-bold">Modifica asset</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Titolo*</label>
            <input
              value={title}
              onChange={e => setTitle(e.target.value)}
              data-testid="edit-title"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Descrizione</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Categoria</label>
            <select
              value={category}
              onChange={e => setCategory(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
            >
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 p-5 border-t border-slate-200 bg-slate-50">
          <button onClick={onClose} className="px-4 py-2 border border-slate-200 rounded-lg text-sm font-semibold">Annulla</button>
          <button
            onClick={submit}
            disabled={saving}
            data-testid="edit-submit"
            className="px-5 py-2 bg-[#D44E20] text-white rounded-lg text-sm font-semibold hover:shadow-md disabled:opacity-50"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : 'Salva'}
          </button>
        </div>
      </div>
    </div>
  );
}
